import { useState } from 'react';

import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import { Button } from '@src/components/ui';

import { getStripePromise } from './billingProvider';
import { Modal } from './controls';


/**
 * Run the Stripe confirmation for the Payment Element and drive the form's state
 * machine. Extracted as a plain async function (no hooks) so it can be unit-tested
 * in the project's node-env Vitest without rendering Stripe's iframes — the React
 * form below is a thin shell over it.
 *
 * The card is tokenized by Stripe.js in the browser; `redirect: 'if_required'`
 * keeps the user in-app unless the payment method mandates a redirect (e.g. 3-DS
 * on some cards). `clientSecret` is supplied to `<Elements>`, so the confirm call
 * does not repeat it. Success → `onDone()` (the caller polls for activation);
 * a `{ error }` result surfaces its message inline (no activation).
 */
async function submitBillingPayment({ stripe, elements, mode, setBusy, setError, onDone }) {
  setError(null);
  if (!stripe || !elements) {
    setError('Payment form is still loading — try again in a moment.');
    return;
  }
  setBusy(true);
  const result = mode === 'setup'
    ? await stripe.confirmSetup({ elements, redirect: 'if_required' })
    : await stripe.confirmPayment({ elements, redirect: 'if_required' });
  setBusy(false);
  if (result?.error) {
    setError(result.error.message || 'Payment could not be completed.');
    return;
  }
  onDone();
}


/** Inner Elements form — MUST be a child of `<Elements>` so `useStripe`/
 * `useElements` resolve. Renders the Payment Element, an inline error, and the
 * confirm CTA. */
function PaymentForm({ mode, submitLabel, onDone, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    submitBillingPayment({ stripe, elements, mode, setBusy, setError, onDone });
  };

  return (
    <form className="flex flex-col gap-3.5 pb-1" onSubmit={handleSubmit}>
      <PaymentElement />

      {error
        ? <span className="text-table font-medium text-danger">{error}</span>
        : null}

      <div className="flex justify-end gap-2.5 pt-1">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>

        <Button type="submit" disabled={!stripe} loading={busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

PaymentForm.displayName = 'PaymentForm';


/**
 * Embedded Stripe checkout modal (M34). Wraps the Payment Element in `<Elements>`
 * bound to the server-issued `clientSecret`. `mode="subscribe"` confirms the first
 * invoice's PaymentIntent; `mode="setup"` confirms a SetupIntent (update card).
 * On success `onDone()` fires — the caller is responsible for polling the billing
 * query until the webhook flips the subscription to active. Reuses the shared
 * `Modal` shell (focus trap / ESC / scroll-lock) like the other Settings modals.
 */
function BillingPaymentModal({ mode = 'subscribe', clientSecret, publishableKey, onDone, onClose }) {
  const stripePromise = getStripePromise(publishableKey);
  const isSetup = mode === 'setup';

  return (
    <Modal
      title={isSetup ? 'Update payment method' : 'Confirm your subscription'}
      subtitle={isSetup
        ? 'Enter a new card. Your card details go straight to Stripe and never touch our servers.'
        : 'Enter your card to activate the plan. Your card details go straight to Stripe and never '
          + 'touch our servers.'}
      onClose={onClose}
    >
      <Elements stripe={stripePromise} options={{ clientSecret }}>
        <PaymentForm
          submitLabel={isSetup ? 'Save card' : 'Pay & activate'}
          mode={mode}
          onDone={onDone}
          onClose={onClose}
        />
      </Elements>
    </Modal>
  );
}

BillingPaymentModal.displayName = 'BillingPaymentModal';

export default BillingPaymentModal;
export { submitBillingPayment };
